import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "../Common/Button/Button";
import IconButton from "../Common/Button/IconButton";
import Dialog from "../Common/Dailog/Dailog";
import AppTabs from "../Common/Tabs/AppTabs";
import FormInput from "../Common/FormInputs/MuiTextField";
import AssetFields from "./AssetFields";
import CtaEditor from "./CtaEditor";
import WysiwygEditorField from "./WysiwygEditorField";
import FormFieldRow from "./FormFieldRow";
import {
  createEmptyCarouselScreen,
  IN_APP_NOTIFICATION_VALIDATIONS,
  inAppPrimaryActionSx,
  pageAssetOnlyGridSx,
  pageFormShortInputSx,
} from "../../constants/inAppNotificationConstant";
import {
  getNotificationRichTextHelperText,
  normalizeAssetCustomProperties,
  validateCtaField,
  validateRichTextBodyField,
  validateScreenTitleField,
  validateCarouselScreensField,
} from "../../utils/inAppNotificationUtils";

const screenTabLabel = (screen, index) => {
  const title = screen.title?.trim();
  return title || `Screen ${index + 1}`;
};

const CarouselScreensSection = ({
  screens,
  onChange,
  errors = {},
  hasAttemptedSave = false,
  feedbackHandler,
  embedded = false,
  readOnly = false,
  page = false,
}) => {
  const [activeScreen, setActiveScreen] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, index: null });
  const inputSx = page ? pageFormShortInputSx : undefined;

  useEffect(() => {
    if (activeScreen >= screens.length && screens.length > 0) {
      setActiveScreen(screens.length - 1);
    }
    if (screens.length === 0) {
      setActiveScreen(0);
    }
  }, [screens.length, activeScreen]);

  const addScreen = () => {
    onChange([...screens, createEmptyCarouselScreen()]);
    setActiveScreen(screens.length);
  };

  const removeScreen = (index) => {
    if (screens.length <= IN_APP_NOTIFICATION_VALIDATIONS.minScreensPerCarousel) return;
    onChange(screens.filter((_, i) => i !== index));
    if (activeScreen >= index && activeScreen > 0) {
      setActiveScreen((prev) => prev - 1);
    }
  };

  const requestRemoveScreen = (index) => {
    if (screens.length <= IN_APP_NOTIFICATION_VALIDATIONS.minScreensPerCarousel) return;
    setDeleteDialog({ open: true, index });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, index: null });
  };

  const confirmRemoveScreen = () => {
    if (deleteDialog.index == null) return;
    removeScreen(deleteDialog.index);
    closeDeleteDialog();
  };

  const pendingDeleteScreen =
    deleteDialog.index != null ? screens[deleteDialog.index] : null;
  const pendingDeleteLabel = pendingDeleteScreen
    ? screenTabLabel(pendingDeleteScreen, deleteDialog.index)
    : "this screen";

  const updateScreen = (index, field, value) => {
    onChange((currentScreens) =>
      currentScreens.map((screen, i) =>
        i === index ? { ...screen, [field]: value } : screen,
      ),
    );
  };

  const updateScreenAssets = (index, fieldOrPatch, value) => {
    onChange((currentScreens) =>
      currentScreens.map((screen, i) => {
        if (i !== index) return screen;

        const nextAssets =
          fieldOrPatch && typeof fieldOrPatch === "object" && value === undefined
            ? {
                ...screen.assets,
                ...fieldOrPatch,
                customProperties: normalizeAssetCustomProperties(
                  fieldOrPatch.customProperties ?? screen.assets?.customProperties,
                ),
              }
            : {
                ...screen.assets,
                [fieldOrPatch]: value,
              };

        return {
          ...screen,
          assets: nextAssets,
        };
      }),
    );
  };

  const screenTabs = screens.map((screen, index) => ({
    label: screenTabLabel(screen, index),
  }));

  const index = Math.min(activeScreen, Math.max(0, screens.length - 1));
  const screen = screens[index];
  const screensError = hasAttemptedSave ? validateCarouselScreensField(screens) : "";
  const screenTitleError = screen
    ? validateScreenTitleField(screen.title, { required: hasAttemptedSave })
    : "";
  const screenDescError = screen
    ? validateRichTextBodyField(screen.desc, {
        required: hasAttemptedSave,
        maxLength: IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
      })
    : "";
  const screenCtaError = screen ? validateCtaField(screen.cta) : "";
  const screenDescHelper = getNotificationRichTextHelperText(screen?.desc || "", {
    validationError: screenDescError,
    maxLength: IN_APP_NOTIFICATION_VALIDATIONS.screenDesc.maxLength,
  });
  const canRemoveScreen =
    !readOnly && screens.length > IN_APP_NOTIFICATION_VALIDATIONS.minScreensPerCarousel;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 0.75,
          mb: 0.75,
        }}
      >
        {!embedded && (
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
            Carousel Screens *
          </Typography>
        )}
        {embedded && <Box sx={{ flex: 1, minWidth: 0 }} />}
        {!readOnly && (
          <Button
            variant="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={addScreen}
            sx={embedded ? inAppPrimaryActionSx : { alignSelf: { xs: "stretch", sm: "auto" } }}
          >
            Add Screen
          </Button>
        )}
      </Box>

      {(screensError || errors.screens) && (
        <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
          {screensError || errors.screens}
        </Typography>
      )}

      {screens.length === 0 ? (
        <Box
          sx={{
            py: 1.5,
            px: 1.5,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Add at least one carousel screen
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 0.75,
              mb: 1,
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <AppTabs
                value={index}
                onChange={(e, v) => setActiveScreen(v)}
                tabs={screenTabs}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                tabMinWidth="auto"
                appearance="accent"
              />
            </Box>
            {canRemoveScreen && (
              <IconButton
                variant="danger"
                size="small"
                tooltip="Remove screen"
                icon={<DeleteIcon fontSize="small" />}
                onClick={() => requestRemoveScreen(index)}
                sx={{ flexShrink: 0 }}
                aria-label="Remove screen"
              />
            )}
          </Box>

          {screen && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Grid2 container spacing={1.25} sx={{ width: "100%", m: 0, alignItems: "flex-start" }}>
                <Grid2 size={{ xs: 12, md: 6, lg: 5 }}>
                  <FormFieldRow
                    stacked={page}
                    compact={!page}
                    page={!page}
                    label="Screen title"
                    required
                    error={screenTitleError}
                  >
                    <FormInput
                      id={`screen-${index}-title`}
                      size="small"
                      fullWidth
                      value={screen.title}
                      onChange={(e) => updateScreen(index, "title", e.target.value)}
                      placeholder="Headline for this slide"
                      error={false}
                      touched="false"
                      sx={inputSx}
                      disabled={readOnly}
                    />
                  </FormFieldRow>
                </Grid2>
              </Grid2>

              <FormFieldRow
                stacked={page}
                compact={!page}
                page={!page}
                label="Description"
                required
                error={screenDescError}
              >
                <WysiwygEditorField
                  value={screen.desc}
                  onChange={(html) => updateScreen(index, "desc", html)}
                  error={false}
                  helperText={!readOnly && !screenDescError ? screenDescHelper.text : undefined}
                  helperTone="default"
                  height={page ? 88 : 100}
                  hideLabel
                  readOnly={readOnly}
                />
              </FormFieldRow>

              {page ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box sx={pageAssetOnlyGridSx}>
                    <AssetFields
                      page
                      paired
                      assets={screen.assets}
                      alignActionRows={(screen.cta || []).length > 0}
                      onChange={(fieldOrPatch, value) =>
                        updateScreenAssets(index, fieldOrPatch, value)
                      }
                      errors={{
                        asset: errors[`screenAsset_${index}`],
                        assetCustomProperties:
                          errors[`screenAssetCustomProperties_${index}`],
                      }}
                      idPrefix={`screen-${index}-asset`}
                      feedbackHandler={feedbackHandler}
                      readOnly={readOnly}
                    />
                  </Box>
                  <CtaEditor
                    page
                    ctas={screen.cta}
                    onChange={(cta) => updateScreen(index, "cta", cta)}
                    maxCtas={IN_APP_NOTIFICATION_VALIDATIONS.maxCtasPerScreen}
                    title="Screen CTAs (optional)"
                    idPrefix={`screen-${index}-cta`}
                    error={screenCtaError || errors[`screenCta_${index}`]}
                    readOnly={readOnly}
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%" }}>
                  <AssetFields
                    compact
                    assets={screen.assets}
                    onChange={(fieldOrPatch, value) =>
                      updateScreenAssets(index, fieldOrPatch, value)
                    }
                    errors={{
                      asset: errors[`screenAsset_${index}`],
                      assetCustomProperties:
                        errors[`screenAssetCustomProperties_${index}`],
                    }}
                    idPrefix={`screen-${index}-asset`}
                    feedbackHandler={feedbackHandler}
                    readOnly={readOnly}
                  />
                  <CtaEditor
                    compact
                    ctas={screen.cta}
                    onChange={(cta) => updateScreen(index, "cta", cta)}
                    maxCtas={IN_APP_NOTIFICATION_VALIDATIONS.maxCtasPerScreen}
                    title="Screen CTAs (optional)"
                    idPrefix={`screen-${index}-cta`}
                    error={screenCtaError || errors[`screenCta_${index}`]}
                    readOnly={readOnly}
                  />
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        title="Delete Screen"
        maxWidth="xs"
        fullWidth
        showCloseButton
        actions={[
          { label: "Cancel", variant: "ghost", onClick: closeDeleteDialog },
          { label: "Delete", variant: "primary", onClick: confirmRemoveScreen },
        ]}
      >
        <Typography variant="body2">
          Are you sure you want to delete &quot;{pendingDeleteLabel}&quot;? At least one carousel
          screen must remain.
        </Typography>
      </Dialog>
    </Box>
  );
};

export default CarouselScreensSection;
